import { v4 as uuid } from "uuid";
import { localStorageGetWithFallback, localStorageSet } from "../lib/storage";
import { ValueOf } from "../lib/ts-helpers";
import { EmbedViewEvent } from "./types";

export interface UTMObject {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content?: string;
}

export function createUTMObject(obj: {
  utm_campaign: string;
  utm_content?: string;
}): UTMObject {
  return {
    utm_source: "partner",
    utm_medium: "embed",
    ...obj,
  };
}

/**
 * The older elements all used static UTM values. The workflow element uses
 * multiple UTMs depending on its configuration, so this entire object is kind
 * of a bad idea going forward.
 * @deprecated
 */
export const utmObjects = {
  appDirectoryElement: createUTMObject({
    utm_campaign: "app_directory_element",
  }),
  zapManagerElement: createUTMObject({
    utm_campaign: "zap_manager_element",
  }),
  zapTemplatesElement: createUTMObject({
    utm_campaign: "zap_templates_element",
  }),
  fullZapierExperience: createUTMObject({
    utm_campaign: "full_zapier_experience",
  }),
  /**
   * This value is only used as a fallback in the case where a component needs
   * an initial value for a `UTMObject` that's received as a prop. We shouldn't
   * use this otherwise, since we can give more specific `utm_campaign` values.
   * */
  workflowElement__fallbackValue: createUTMObject({
    utm_campaign: "wfe_unknown",
  }),
} as const;

export const embedViewSchemaName = "partner.embed.EmbedViewEvent";

// These are NOT Avro schema names! These are internal identifiers that map to
// the above UTM campaigns. This level of indirection is not necessary, but I'm
// also not going to refactor the event system further at this moment.
//
// Sage Fennel (2022-07-12)
// @team-partner-sharing
export const eventNames = {
  appDirectoryLoad: "app_directory__impression",
  zapManagerLoad: "zap_manager__impression",
  zapManagerLogin: "zap_manager__my_zaps_load",
  zapTemplatesLoad: "zap_templates__impression",
  fullZapierExperienceLoad: "full_zapier_experience__impression",
  workflowElementLoad: "workflow_element__impression",
} as const;

type EventName = ValueOf<typeof eventNames>;

const isInIframe = window.self !== window.top;

function getLocation() {
  try {
    return (top || window).location.href;
  } catch {
    return location.href;
  }
}

function getVisitorID() {
  return localStorageGetWithFallback("visitor_id", () => {
    const id = uuid();
    localStorageSet("visitor_id", id);
    return id;
  });
}

/**
 * Due to event system limitations, we can only send strings as values in this
 * configuration map. We'll preserve strings, numbers, and booleans, converting
 * them to strings. All other values will be removed.
 */
export function formatEmbedConfiguration(
  embedConfiguration: Record<string, unknown>,
): EmbedViewEvent["embed_configuration"] {
  return Object.fromEntries(
    Object.entries(embedConfiguration).flatMap(([key, value]) => {
      if (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        return [[key, String(value)]];
      }
      return [];
    }),
  );
}

export interface EmbedViewEventOptions {
  clientId: string;
  partnerName: string | null;
  eventName: EventName;
  referrer: string;
  embedConfiguration: EmbedViewEvent["embed_configuration"];
  utmObject: UTMObject;
}

export function createEmbedViewEvent({
  clientId,
  partnerName,
  eventName,
  referrer,
  embedConfiguration,
  utmObject,
}: EmbedViewEventOptions): EmbedViewEvent {
  return {
    timestamp_ms: Date.now(),
    event_name: eventName,
    url_address: getLocation(),
    visitor_id: getVisitorID(),
    referer: referrer,
    iframe_flag: isInIframe,
    client_id: clientId,
    partner_name: partnerName,
    user_agent: navigator.userAgent,
    embed_configuration: embedConfiguration,
    screen_size: `${window.screen.width}x${window.screen.height}`,
    ...utmObject,
  };
}
